import { AnyAction, ThunkAction } from '@reduxjs/toolkit';
import { resetPuntataAllScommesse, updatePuntataAllScommesse } from 'features/carrello/carrelloEditQuotaInput/actions';
import { updateErroriSistemiAdded } from 'features/carrello/carrelloEditQuotaInputSistema/actions';
import {
  selectBonusFromType,
  selectDefaultPuntata,
  selectDefaultSistemaValue,
  selectSliceTicketByType,
  selectVariationByTipo,
} from 'features/carrello/carrelloSelectors';
import { PaymentMethodEnum, selectedImportoType } from 'features/carrello/carrelloSlice';
import { TicketErroreSistema } from 'features/carrello/types';
import { getSliceByTipoTicket, removeLeadingZeros } from 'features/carrello/utils';
import { selectTipoVendita } from 'features/ippica/components/ippicaTicket/ippicaTicketSelectors';
import { hasKey, isDivisible } from 'features/sport';
import { RootState } from 'lib/centralStore';
import { CartTipoVenditaEnum, MovimentiTipoScommessaEnum } from 'types/swagger';
import { isNotEmpty } from 'utility/functions';

const getIppicaValue = (state: RootState) => {
  const tipoVendita = selectTipoVendita(state);
  if (tipoVendita === CartTipoVenditaEnum.TOTALIZZATORE || tipoVendita === CartTipoVenditaEnum.NAZIONALE) {
    return String(state.ippicaTicket.moltiplicatore!);
  }
  return state.ippicaTicket.puntataSingolaMultipla;
};

export const updateImporto =
  (value: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const state = getState();
    const { tipo, selectedIdSistema } = state.carrello;
    const { actions } = getSliceByTipoTicket(tipo);
    const { ticket, puntataSingolaMultipla, puntataPerScommessa, puntataAllScommesse, errori } =
      selectSliceTicketByType(state, tipo);

    if (ticket) {
      if (ticket.sistema) {
        const currentValue = selectedIdSistema ? puntataPerScommessa[selectedIdSistema] : puntataAllScommesse;

        const [_, decimalPart] = `${currentValue}.`.split('.');
        if (decimalPart.length > 1) return;

        const positionalValue = removeLeadingZeros(`${!isNotEmpty(currentValue) ? '' : currentValue}${value}`);
        if (selectedIdSistema) {
          dispatch(
            actions.puntataPerScommesseSistemaUpdated({
              idSistema: selectedIdSistema,
              puntata: positionalValue,
              isPlain: true,
            })
          );
          if (!isDivisible(+positionalValue)) {
            dispatch(
              updateErroriSistemiAdded(selectedIdSistema, TicketErroreSistema.IncrementoImportoSistemaNonCorretto)
            );
          } else {
            if (hasKey(errori.erroriSistema, [selectedIdSistema])) {
              dispatch(
                actions.erroriSistemiRemoved({
                  idSistema: selectedIdSistema,
                  errore: TicketErroreSistema.IncrementoImportoSistemaNonCorretto,
                })
              );
            }
          }
          dispatch(resetPuntataAllScommesse());
        } else {
          dispatch(updatePuntataAllScommesse(positionalValue!));
        }
      } else {
        let cartInput = puntataSingolaMultipla;
        if (state.carrello.tipo === MovimentiTipoScommessaEnum.Ippica) {
          cartInput = getIppicaValue(state);
        }

        const [_, decimalPart] = `${cartInput}.`.split('.');
        if (decimalPart.length > 1) return;

        const positionalValue = removeLeadingZeros(`${!isNotEmpty(cartInput) ? '' : cartInput}${value}`);
        dispatch(actions.puntataSingolaMultiplaUpdated(positionalValue!));
        dispatch(selectedImportoType(PaymentMethodEnum.None));
      }
    }
  };

export const deleteLastDigit = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch, getState) => {
  const state = getState();
  const { tipo, selectedIdSistema } = state.carrello;
  const { actions } = getSliceByTipoTicket(tipo);
  const { ticket, puntataSingolaMultipla, puntataPerScommessa, puntataAllScommesse, errori } = selectSliceTicketByType(
    state,
    tipo
  );

  if (ticket) {
    if (ticket.sistema) {
      const positionalValue = `${
        selectedIdSistema ? puntataPerScommessa[selectedIdSistema] : puntataAllScommesse
      }`.slice(0, -1);
      if (selectedIdSistema) {
        dispatch(
          actions.puntataPerScommesseSistemaUpdated({
            idSistema: selectedIdSistema!,
            puntata: positionalValue,
            isPlain: true,
          })
        );
        if (!isDivisible(+positionalValue)) {
          dispatch(
            updateErroriSistemiAdded(selectedIdSistema, TicketErroreSistema.IncrementoImportoSistemaNonCorretto)
          );
        } else {
          if (hasKey(errori.erroriSistema, [selectedIdSistema])) {
            dispatch(
              actions.erroriSistemiRemoved({
                idSistema: selectedIdSistema,
                errore: TicketErroreSistema.IncrementoImportoSistemaNonCorretto,
              })
            );
          }
        }
        dispatch(resetPuntataAllScommesse);
      } else {
        dispatch(updatePuntataAllScommesse(positionalValue!));
      }
    } else {
      let cartInput = puntataSingolaMultipla;
      if (state.carrello.tipo === MovimentiTipoScommessaEnum.Ippica) {
        cartInput = getIppicaValue(state);
      }
      const positionalValue = `${cartInput ?? 0}`.slice(0, -1);
      dispatch(actions.puntataSingolaMultiplaUpdated(positionalValue));
      dispatch(selectedImportoType(PaymentMethodEnum.None));
    }
  }
};

export const addDot = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch, getState) => {
  const state = getState();
  const { tipo, selectedIdSistema } = state.carrello;
  const { actions } = getSliceByTipoTicket(tipo);
  const { ticket, puntataSingolaMultipla, puntataPerScommessa, puntataAllScommesse } = selectSliceTicketByType(
    state,
    tipo
  );

  if (ticket) {
    if (ticket.sistema) {
      const cartInput = selectedIdSistema ? puntataPerScommessa[selectedIdSistema] : puntataAllScommesse;
      if (`${cartInput}`.includes('.')) return;
      const positionalValue = removeLeadingZeros(`${cartInput}.`);
      if (selectedIdSistema) {
        dispatch(
          actions.puntataPerScommesseSistemaUpdated({
            idSistema: selectedIdSistema!,
            puntata: positionalValue,
            isPlain: true,
          })
        );
        dispatch(resetPuntataAllScommesse());
      } else {
        dispatch(updatePuntataAllScommesse(positionalValue));
      }
    } else {
      let cartInput = puntataSingolaMultipla;
      if (state.carrello.tipo === MovimentiTipoScommessaEnum.Ippica) {
        cartInput = getIppicaValue(state);
      }
      if (`${cartInput}`.includes('.')) return;

      const positionalValue = removeLeadingZeros(`${cartInput}.`);
      dispatch(actions.puntataSingolaMultiplaUpdated(positionalValue));
      dispatch(selectedImportoType(PaymentMethodEnum.None));
    }
  }
};

export const step =
  (type: 'increment' | 'decrement'): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const state = getState();
    const { tipo, selectedIdSistema } = state.carrello;
    const { actions } = getSliceByTipoTicket(tipo);
    const stepVariation = selectVariationByTipo(state, tipo);
    const { ticket, puntataSingolaMultipla, puntataPerScommessa, puntataAllScommesse } = selectSliceTicketByType(
      state,
      tipo
    );

    if (ticket) {
      if (ticket.sistema) {
        const defaultSistemaValue = selectDefaultSistemaValue(state);
        let currentSistemaPuntata = selectedIdSistema ? puntataPerScommessa[selectedIdSistema] : puntataAllScommesse;
        currentSistemaPuntata = !isNotEmpty(currentSistemaPuntata)
          ? String(defaultSistemaValue)
          : currentSistemaPuntata;
        const value =
          type === 'increment' ? +currentSistemaPuntata + stepVariation : +currentSistemaPuntata - stepVariation;
        if (selectedIdSistema) {
          dispatch(
            actions.puntataPerScommesseSistemaUpdated({
              idSistema: selectedIdSistema!,
              puntata: String(value.toFixed(2)),
              isPlain: true,
            })
          );
          dispatch(resetPuntataAllScommesse());
        } else {
          dispatch(updatePuntataAllScommesse(value.toFixed(2)));
        }
      } else {
        let cartInput = puntataSingolaMultipla;
        if (state.carrello.tipo === MovimentiTipoScommessaEnum.Ippica) {
          cartInput = getIppicaValue(state);
        }
        const newValue = type === 'increment' ? +cartInput + stepVariation : +cartInput - stepVariation;
        const positionalValue = newValue.toFixed(2);
        dispatch(actions.puntataSingolaMultiplaUpdated(positionalValue));
        dispatch(selectedImportoType(PaymentMethodEnum.None));
      }
    }
  };

export const updatePaymentValue =
  (type: PaymentMethodEnum): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const state = getState();
    const { saldoReal, bonus, saldoGold } = selectBonusFromType(state);
    const { actions } = getSliceByTipoTicket(state.carrello.tipo);
    const { ticket } = selectSliceTicketByType(state, state.carrello.tipo);

    const roundToNearestFiveCents = (amount: number): number => {
      return Math.floor(amount * 20) / 20;
    };

    if (ticket?.sistema && type === PaymentMethodEnum.Gold) {
      if (state.carrello.tipo !== MovimentiTipoScommessaEnum.Sport) throw new Error('Tipo non supportato');

      const selectedIdSistema = state.carrello.selectedIdSistema;
      dispatch(
        actions.puntataPerScommesseSistemaUpdated({
          idSistema: selectedIdSistema!,
          puntata: String(roundToNearestFiveCents(saldoGold / 100)),
          isPlain: true,
        })
      );
    } else {
      switch (type) {
        case PaymentMethodEnum.Cash:
          dispatch(
            actions.puntataSingolaMultiplaUpdated(String(roundToNearestFiveCents(saldoReal / 100 + bonus / 100)))
          );
          break;
        case PaymentMethodEnum.Gold:
          dispatch(actions.puntataSingolaMultiplaUpdated(String(roundToNearestFiveCents(saldoGold / 100))));
          break;
      }
    }
    dispatch(selectedImportoType(type));
  };

export const restorePreviousValue = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch, getState) => {
  const state = getState();

  const { actions } = getSliceByTipoTicket(state.carrello.tipo);
  const { ticket } = selectSliceTicketByType(state, state.carrello.tipo);

  if (ticket?.sistema) {
    const selectedIdSistema = state.carrello.selectedIdSistema;
    dispatch(
      actions.puntataPerScommesseSistemaUpdated({
        idSistema: selectedIdSistema!,
        puntata: String(selectDefaultSistemaValue(state)),
        isPlain: true,
      })
    );
  } else {
    const defaultPuntataSingola = selectDefaultPuntata(state);
    dispatch(actions.puntataSingolaMultiplaUpdated(String(defaultPuntataSingola)));
  }
};
